import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import TimezoneSelect from "../../../../shared/ui/form/TimezoneSelect";
import { Center, CenterUpdateInput, useUpdateCenter } from "../../../center";
import CenterLogoField from "./CenterLogoField";

interface Props {
    center: Center;
}

/**
 * The centre's own details.
 *
 * Everything except the name is optional, and stays optional after sign-up: a
 * centre that has not registered its tax id yet is a centre, and a form that
 * refused to save without one would be holding the whole practice hostage to
 * paperwork.
 *
 * The vertical (therapy or training) is shown and not editable. It is declared
 * once, at sign-up; switching it after the centre holds people is a data
 * question rather than a settings toggle.
 */
export default function CenterDetailsCard({ center }: Props) {
    const { t } = useTranslation();
    const updateCenter = useUpdateCenter(center.id);

    const [form, setForm] = useState<CenterUpdateInput>({});
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setForm({
            name: center.name,
            legal_name: center.legal_name ?? "",
            tax_id: center.tax_id ?? "",
            address: center.address ?? "",
            contact_email: center.contact_email ?? "",
            contact_phone: center.contact_phone ?? "",
            timezone: center.timezone,
        });
    }, [center]);

    const set = (field: keyof CenterUpdateInput) => (value: string) => {
        setForm((previous) => ({ ...previous, [field]: value }));
        setSaved(false);
    };

    const handleSave = async () => {
        setError(null);
        setSaved(false);
        try {
            await updateCenter.mutateAsync(form);
            setSaved(true);
        } catch {
            setError(t("center.settings.saveError"));
        }
    };

    const nameIsBlank = !(form.name ?? "").trim();

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h2 className="mb-1 text-lg font-semibold text-content-primary">
                {t("center.settings.title")}
            </h2>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                {center.type === "training"
                    ? t("center.settings.subtitleTraining")
                    : t("center.settings.subtitleTherapy")}
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
                <CenterLogoField center={center} />
                <div className="sm:col-span-2">
                    <Label htmlFor="center-name">{t("center.fields.name")}</Label>
                    <Input
                        id="center-name"
                        name="name"
                        value={form.name ?? ""}
                        onChange={(e) => set("name")(e.target.value)}
                        error={nameIsBlank}
                        hint={nameIsBlank ? t("center.settings.nameRequired") : undefined}
                        required
                    />
                </div>

                <div>
                    <Label htmlFor="center-legal-name">{t("center.fields.legalName")}</Label>
                    <Input
                        id="center-legal-name"
                        name="legal_name"
                        value={form.legal_name ?? ""}
                        onChange={(e) => set("legal_name")(e.target.value)}
                    />
                </div>

                <div>
                    <Label htmlFor="center-tax-id">{t("center.fields.taxId")}</Label>
                    <Input
                        id="center-tax-id"
                        name="tax_id"
                        value={form.tax_id ?? ""}
                        onChange={(e) => set("tax_id")(e.target.value)}
                    />
                </div>

                <div className="sm:col-span-2">
                    <Label htmlFor="center-address">{t("center.fields.address")}</Label>
                    <Input
                        id="center-address"
                        name="address"
                        value={form.address ?? ""}
                        onChange={(e) => set("address")(e.target.value)}
                    />
                </div>

                <div>
                    <Label htmlFor="center-contact-email">{t("center.fields.contactEmail")}</Label>
                    <Input
                        id="center-contact-email"
                        name="contact_email"
                        type="email"
                        value={form.contact_email ?? ""}
                        onChange={(e) => set("contact_email")(e.target.value)}
                    />
                </div>

                <div>
                    <Label htmlFor="center-contact-phone">{t("center.fields.contactPhone")}</Label>
                    <Input
                        id="center-contact-phone"
                        name="contact_phone"
                        value={form.contact_phone ?? ""}
                        onChange={(e) => set("contact_phone")(e.target.value)}
                    />
                </div>

                <div className="sm:col-span-2">
                    <Label htmlFor="center-timezone">{t("center.fields.timezone")}</Label>
                    <TimezoneSelect
                        id="center-timezone"
                        name="timezone"
                        value={form.timezone ?? ""}
                        onChange={set("timezone")}
                        placeholder={t("center.fields.timezonePlaceholder")}
                    />
                </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
                <Button
                    onClick={handleSave}
                    disabled={nameIsBlank || updateCenter.isPending}
                    loading={updateCenter.isPending}
                >
                    {t("common.save")}
                </Button>
                {saved && (
                    <span role="status" className="text-sm text-green-600 dark:text-green-400">
                        {t("center.settings.saved")}
                    </span>
                )}
                {error && (
                    <span role="alert" className="text-sm text-red-600 dark:text-red-400">
                        {error}
                    </span>
                )}
            </div>
        </section>
    );
}
